import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, MapPin, Globe, Clock } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import GoogleReviewBadge from './GoogleReviewBadge';

/**
 * ExperienceCard - Unified card for all marketplace experiences.
 * Inset rounded image (Featured/Discover style) + Trending-style text layout:
 * location top, big title, "by operator", duration left / big blue price right.
 * Used in: Featured, Discover, Trending, Search, You might also like, Operator profile
 */
const ExperienceCard = ({ service, featured = false, showDistance = false, testId, imgSrc, eager = false, badge }) => {
  const navigate = useNavigate();

  if (!service) return null;

  const formatPrice = (price, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price || 0);
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '—';
    if (minutes >= 1440) {
      const days = Math.floor(minutes / 1440);
      return `${days} day${days > 1 ? 's' : ''}`;
    }
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return mins > 0 ? `${hours}h ${mins}m` : `${hours} hour${hours > 1 ? 's' : ''}`;
    }
    return `${minutes}m`;
  };

  const mainImage = imgSrc || service.mainImage || (service.images && service.images[0]);
  const operator = service.operator || {};
  const hasReviews = operator.reviewCount > 0 && operator.rating;

  return (
    <Card
      onClick={() => navigate(`/experience/${service.id}`)}
      className="group cursor-pointer border-0 rounded-3xl bg-white p-3 shadow-[0_8px_24px_rgba(0,0,0,0.05)] hover:shadow-[0_16px_40px_rgba(0,0,0,0.10)] hover:-translate-y-1 transition-[box-shadow,transform] duration-300"
      data-testid={testId || `experience-card-${service.id}`}
    >
      {/* Image — inset with rounded corners, slightly more vertical */}
      <div className="aspect-[5/4] relative overflow-hidden rounded-2xl bg-gray-100">
        {mainImage ? (
          <img
            src={mainImage}
            alt={service.name}
            loading={eager ? 'eager' : 'lazy'}
            fetchPriority={eager ? 'high' : 'auto'}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full bg-[#04091B] flex items-center justify-center">
            <Globe className="w-10 h-10 text-slate-600" />
          </div>
        )}

        {/* Featured Badge */}
        {featured ? (
          <span className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full bg-white/90 backdrop-blur px-2.5 py-1 text-[11px] font-bold text-gray-900">
            <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
            Featured
          </span>
        ) : badge ? (
          <span className="absolute top-2 left-2 rounded-full bg-white/90 backdrop-blur px-2.5 py-1 text-[11px] font-semibold text-gray-700">
            {badge}
          </span>
        ) : null}
      </div>

      <CardContent className="px-1.5 pt-3.5 pb-1.5">
        {/* Location + rating */}
        <div className="flex items-center justify-between gap-2 text-sm text-gray-500">
          <span className="flex items-center gap-1 min-w-0">
            <MapPin className="w-3.5 h-3.5 shrink-0" />
            <span className="truncate">{service.city || 'Location TBD'}</span>
          </span>
          {operator.googleReviews?.count ? (
            <GoogleReviewBadge reviews={operator.googleReviews} />
          ) : hasReviews ? (
            <span className="inline-flex shrink-0 items-center gap-0.5 text-xs font-bold text-gray-900">
              <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
              {Number(operator.rating).toFixed(1)}
              <span className="font-medium text-gray-400">({operator.reviewCount})</span>
            </span>
          ) : null}
        </div>

        {/* Name — large for scannability */}
        <h3 className="mt-1.5 text-lg font-bold leading-snug text-gray-900 line-clamp-2 group-hover:text-blue-600 transition-colors">
          {service.name}
        </h3>

        {/* Operator */}
        {operator.name && (
          <p className="mt-1 text-sm text-gray-500 truncate">by {operator.name}</p>
        )}

        {/* Duration + price */}
        <div className="mt-3 flex items-center justify-between gap-2 border-t border-gray-100 pt-2.5">
          <span className="flex items-center gap-1 text-sm text-gray-500 min-w-0">
            <Clock className="w-3.5 h-3.5 shrink-0" />
            <span className="truncate">{formatDuration(service.durationMinutes)}</span>
            {showDistance && service.distance && (
              <span className="shrink-0 text-emerald-600">· {Math.round(service.distance)} mi</span>
            )}
          </span>
          <div className="shrink-0 text-right">
            <span className="text-xl font-extrabold tabular-nums text-blue-600">
              {formatPrice(service.price, service.currency)}
            </span>
            {service.priceType === 'per_person' && (
              <span className="text-xs text-gray-400"> /person</span>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ExperienceCard;
